import React from "react";
import {
  Wand2, Loader2, Image as ImageIcon, Video, MessageSquareText,
  ArrowRight, ArrowLeft, BadgeCheck,
} from "lucide-react";
import { BACKEND_URL } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";

const LegendToggle = ({ includeLegend, setLegend }) => (
  <div data-testid="legend-toggle" className="rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-5 mb-4">
    <div className="flex items-center gap-2 mb-1 text-yellow-impact">
      <BadgeCheck size={18} />
      <span className="font-semibold">¿Deseas que en tu diseño aparezca la leyenda "DISTRIBUIDOR INDEPENDIENTE"?</span>
    </div>
    <p className="text-xs text-zinc-400 mb-4">Se aplica a tus textos, imágenes y videos de esta campaña. Recomendado para cumplir las reglas de tu empresa.</p>
    <div className="grid grid-cols-2 gap-3 max-w-sm">
      <button
        type="button" data-testid="legend-yes" onClick={() => setLegend(true)}
        className={`rounded-xl border px-4 py-3 text-sm font-bold transition-all ${includeLegend ? "border-yellow-400 bg-yellow-400 text-black" : "border-white/15 bg-black text-zinc-300 hover:border-yellow-400/50"}`}
      >
        Sí, mostrar leyenda
      </button>
      <button
        type="button" data-testid="legend-no" onClick={() => setLegend(false)}
        className={`rounded-xl border px-4 py-3 text-sm font-bold transition-all ${!includeLegend ? "border-purple-500 bg-purple-600 text-white" : "border-white/15 bg-black text-zinc-300 hover:border-purple-500/50"}`}
      >
        No, sin leyenda
      </button>
    </div>
  </div>
);

const ImageGallery = ({ label, testid, items, getKey, getSrc, onUse }) => (
  <div className="mt-4">
    <p className="text-xs text-zinc-500 mb-2">{label}</p>
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {items.map((it) => (
        <button key={getKey(it)} data-testid={testid} onClick={() => onUse(it)} className="relative group rounded-lg overflow-hidden border border-white/10 h-20">
          <img src={getSrc(it)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = "none"; }} />
          <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] font-semibold transition-opacity">Usar</span>
        </button>
      ))}
    </div>
  </div>
);

export const StepContent = ({ w }) => (
  <div>
    <h2 className="font-display font-bold text-2xl mb-1">3. Genera tu contenido</h2>
    <p className="text-zinc-400 text-sm mb-6">La IA propone textos, imágenes y videos. Genera los que quieras usar.</p>

    <LegendToggle includeLegend={w.includeLegend} setLegend={w.setLegend} />

    {/* Texto */}
    <div className="rounded-xl border border-white/10 bg-[#121212] p-5 mb-4">
      <div className="flex items-center gap-2 mb-3 text-purple-300"><MessageSquareText size={18} /><span className="font-semibold">Texto de la campaña</span></div>
      <Button data-testid="gen-text" onClick={w.genText} disabled={w.loadingText} className="bg-purple-600 hover:bg-purple-500 rounded-xl">
        {w.loadingText ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} {w.textContent ? "Regenerar texto" : "Generar texto"}
      </Button>
      {w.textContent && <div className="mt-3 rounded-xl bg-black border border-white/10 p-4"><p className="text-sm text-zinc-200 whitespace-pre-wrap">{w.textContent.text}</p></div>}
    </div>

    {/* Imágenes */}
    <div className="rounded-xl border border-white/10 bg-[#121212] p-5 mb-4">
      <div className="flex items-center gap-2 mb-3 text-purple-300"><ImageIcon size={18} /><span className="font-semibold">Imágenes {w.includeLegend ? "(con leyenda)" : "(sin leyenda)"}</span><span className="text-xs text-zinc-500 ml-auto">{w.images.length}/3</span></div>
      <Input data-testid="image-prompt" value={w.imagePrompt} onChange={(e) => w.setImagePrompt(e.target.value)} placeholder="Describe la imagen (opcional)" className="bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500 mb-3" />
      <Button data-testid="gen-image" onClick={w.genImage} disabled={w.loadingImage || w.images.length >= 3} className="bg-purple-600 hover:bg-purple-500 rounded-xl disabled:opacity-40">
        {w.loadingImage ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} {w.images.length >= 3 ? "Máximo 3 imágenes" : "Generar imagen con IA"}
      </Button>

      {w.pageImages.length > 0 && (
        <ImageGallery label="Imágenes encontradas en la página (se les añade la leyenda):"
          items={w.pageImages} getKey={(u) => u} getSrc={(u) => u} onUse={(u) => w.addPageImage(u)} />
      )}
      {w.uploadedImages.length > 0 && (
        <ImageGallery label="Imágenes que subiste (se les añade la leyenda):" testid="use-uploaded-image"
          items={w.uploadedImages} getKey={(it) => it.fileId} getSrc={(it) => it.url} onUse={(it) => w.addUploadedImage(it.fileId)} />
      )}

      {w.images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {w.images.map((img) => <img key={img.content_id} src={`${BACKEND_URL}${img.image_url}`} alt="" className="rounded-lg border border-white/10 w-full" />)}
        </div>
      )}
    </div>

    {/* Video */}
    <div className="rounded-xl border border-white/10 bg-[#121212] p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-purple-300"><Video size={18} /><span className="font-semibold">Video corto</span></div>
        <span className="text-[10px] uppercase tracking-wider bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded-full font-bold">Premium</span>
      </div>
      <Button data-testid="gen-video" onClick={w.genVideo} disabled={w.loadingVideo || w.videos.length >= 1} className="bg-purple-600 hover:bg-purple-500 rounded-xl disabled:opacity-40">
        {w.loadingVideo ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} {w.videos.length >= 1 ? "Máximo 1 video" : "Generar video"}
      </Button>
      {w.loadingVideo && <p className="text-xs text-zinc-500 mt-2">Generando video (1-3 min)...</p>}
      {w.videos.map((v) => (
        <div key={v.content_id} className="mt-3">
          {v.status === "done" && v.video_url ? <video src={v.video_url} controls className="rounded-lg w-full max-w-sm" /> :
           v.status === "error" ? <p className="text-xs text-red-400">{v.error || "No se pudo generar el video"}</p> :
           <p className="text-xs text-zinc-500">Procesando video...</p>}
        </div>
      ))}
    </div>

    <div className="flex justify-between mt-8">
      <Button variant="ghost" onClick={w.onBack} className="text-zinc-400"><ArrowLeft size={16} className="mr-2" /> Atrás</Button>
      <Button data-testid="next-3" disabled={!w.textContent && w.images.length === 0} onClick={w.onNext} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5">
        Ir a publicar <ArrowRight size={16} className="ml-2" />
      </Button>
    </div>
  </div>
);
